import React, { useState, useEffect, useCallback } from 'react';
import { TreeNode, LocationOption, LibraryItem } from '../types';
import { ChevronRight, Plus, X, Trash2, Info, Code, MessageSquare, Component, Settings, ListPlus, ArrowUp, ArrowDown, Save, BookmarkPlus } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { LocationEditor } from './LocationEditor';
import { LocationDetailEditor } from './LocationDetailEditor';
import { PostscriptEditor } from './PostscriptEditor';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from 'sonner';
import { createNode } from '../api/nodes';

interface PropertyEditorProps {
  node: TreeNode;
  onUpdate: (id: string, updates: Partial<TreeNode>) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
  onLibraryUpdate?: () => void;
  handleSync?: () => void;
  isReadOnly?: boolean;
  className?: string;
}

export function PropertyEditor({ 
  node, 
  onUpdate,
  onDelete,
  onClose,
  onLibraryUpdate,
  handleSync,
  isReadOnly = false,
  className = ''
}: PropertyEditorProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newSuggestion, setNewSuggestion] = useState('');
  const [showLocationCreator, setShowLocationCreator] = useState(false);
  const [editingLocation, setEditingLocation] = useState<LocationOption | null>(null);
  const [showLocationEditor, setShowLocationEditor] = useState(false);
  const [showPostscriptEditor, setShowPostscriptEditor] = useState(false);
  const [componentType, setComponentType] = useState(node?.data.component?.type || '');
  const [componentProps, setComponentProps] = useState(
    node?.data.component?.props ? JSON.stringify(node.data.component.props, null, 2) : '{}'
  );
  const [showAddToLibraryDialog, setShowAddToLibraryDialog] = useState(false);
  const [libraryNodeName, setLibraryNodeName] = useState(node?.data.name || '');

  // Update local state when node changes
  useEffect(() => {
    if (node) {
      setComponentType(node.data.component?.type || '');
      setComponentProps(node.data.component?.props ? JSON.stringify(node.data.component.props, null, 2) : '{}');
      setLibraryNodeName(node.data.name || '');
    }
  }, [node]);

  if (!node) return null;

  console.log('PropertyEditor rendering with node:', node);
  console.log('Node displayOptions:', node.data.displayOptions);
  console.log('Node component:', node.data.component);

  const handleUpdate = (updates: Partial<TreeNode['data']>) => {
    if (isReadOnly) {
      toast.error('Cannot modify while viewing translations');
      return;
    }
    console.log('PropertyEditor handleUpdate called with:', updates);
    // If we're updating displayOptions, merge them with any existing non-LOCATION options
    if (updates.displayOptions) {
      const existingNonLocationOptions = (node.data.displayOptions || []).filter(opt => opt.type !== "LOCATION");
      updates.displayOptions = [...existingNonLocationOptions, ...updates.displayOptions.filter(opt => opt.type === "LOCATION")];
    }
    
    const dataUpdates = {
      data: {
        ...node.data,
        ...updates
      }
    };
    console.log('Sending update:', dataUpdates);
    onUpdate(node.id, dataUpdates);
    toast.success('Changes saved');
  };

  const handleQuietUpdate = (updates: Partial<TreeNode['data']>) => {
    if (isReadOnly) return;
    const dataUpdates = {
      data: {
        ...node.data,
        ...updates
      }
    };
    console.log('Sending quiet update:', dataUpdates);
    onUpdate(node.id, dataUpdates);
  };

  const handleDirectUpdate = (updates: Partial<TreeNode['data']>) => {
    if (isReadOnly) {
      toast.error('Cannot modify while viewing translations');
      return;
    }
    console.log('PropertyEditor handleDirectUpdate called with:', updates);
    onUpdate(node.id, { data: updates });
  };

  const handleAddToLibrary = async () => {
    const newLibraryItem: Omit<LibraryItem, 'id' | 'createdAt' | 'updatedAt'> = {
      name: libraryNodeName,
      type: 'node',
      category: 'nodes',
      properties: {
        name: libraryNodeName,
        chatMessage: node.data.chatMessage,
        nodeInfo: node.data.nodeInfo,
        html: node.data.html,
        properties: node.data.properties,
        functionCalls: node.data.functionCalls,
        suggestions: node.data.suggestions,
        displayOptions: node.data.displayOptions,
        component: node.data.component,
        postscript: node.data.postscript,
      }
    };

    const response = await createNode(newLibraryItem);
    if (response.error) {
      toast.error(`Failed to add to library: ${response.error}`);
    } else {
      toast.success('Added to library');
      setShowAddToLibraryDialog(false);
      onLibraryUpdate?.();
    }
  };

  const handleComponentUpdate = (updates: any) => {
    onUpdate(node.id, { data: { ...node.data, ...updates } });
  };

  const handleLocationUpdate = (locations: LocationOption[]) => {
    console.log('Handling location update with:', locations);
    // Preserve any existing non-LOCATION display options
    const existingNonLocationOptions = (node.data.displayOptions || []).filter(opt => opt.type !== "LOCATION");
    handleUpdate({ 
      displayOptions: [...existingNonLocationOptions, ...locations] 
    });
  };

  const addProperty = () => {
    if (newKey.trim() && newValue.trim()) {
      const updatedProperties = [...node.data.properties, { key: newKey, value: newValue }];
      handleUpdate({ properties: updatedProperties });
      setNewKey('');
      setNewValue('');
    }
  };

  const deleteProperty = (index: number) => {
    const updatedProperties = node.data.properties.filter((_, i) => i !== index);
    handleUpdate({ properties: updatedProperties });
  };

  const addSuggestion = () => {
    if (newSuggestion.trim()) {
      const updatedSuggestions = [...(node.data.suggestions || []), newSuggestion];
      handleUpdate({ suggestions: updatedSuggestions });
      setNewSuggestion('');
    }
  };

  const deleteSuggestion = (index: number) => {
    const updatedSuggestions = (node.data.suggestions || []).filter((_, i) => i !== index);
    handleUpdate({ suggestions: updatedSuggestions });
  };

  return (
    <>
      <Sheet open={true} onOpenChange={() => onClose()}>
        <SheetContent className={`w-[600px] sm:w-[800px] overflow-hidden ${className}`}>
          <div className="flex flex-col space-y-4">
            {/* Header */}
            <div className="flex items-center justify-end border-b pb-4">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSync}
                  disabled={isReadOnly}
                  title={isReadOnly ? "Cannot save while viewing translations" : "Save changes"}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddToLibraryDialog(true)}
                >
                  <BookmarkPlus className="h-4 w-4 mr-2" />
                  Add to Library
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>

            {/* Node Name */}
            <div className="space-y-2 px-1">
              <Label>Node Name</Label>
              <Input
                value={node.data.name}
                onChange={(e) => handleQuietUpdate({ name: e.target.value })}
                onBlur={(e) => handleUpdate({ name: e.target.value })}
                className="text-lg font-semibold"
                placeholder="Enter node name..."
              />
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="h-[calc(100vh-180px)]">
              <TabsList className="grid w-full grid-cols-4 p-1 bg-gray-100 dark:bg-gray-800 rounded-t-lg">
                <TabsTrigger value="info" className="flex items-center gap-2 px-3 py-2 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700">
                  <Info className="h-4 w-4" />
                  <span className="hidden sm:inline">Info</span>
                </TabsTrigger>
                <TabsTrigger value="chat" className="flex items-center gap-2 px-3 py-2 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700">
                  <MessageSquare className="h-4 w-4" />
                  <span className="hidden sm:inline">Chat</span>
                </TabsTrigger>
                <TabsTrigger value="component" className="flex items-center gap-2 px-3 py-2 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700">
                  <Component className="h-4 w-4" />
                  <span className="hidden sm:inline">Component</span>
                </TabsTrigger>
                <TabsTrigger value="code" className="flex items-center gap-2 px-3 py-2 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700">
                  <Code className="h-4 w-4" />
                  <span className="hidden sm:inline">Code</span>
                </TabsTrigger>
              </TabsList>

              <ScrollArea className="mt-4 px-6 max-h-[calc(100vh-160px)] overflow-y-auto rounded-b-lg">
                <TabsContent value="info" className="space-y-6 p-4 rounded-b-lg">
                  <div className="space-y-2">
                    <Label>Node Info</Label>
                    <Textarea
                      value={node.data.nodeInfo}
                      onChange={(e) => handleUpdate({ nodeInfo: e.target.value })}
                      className="h-32"
                      placeholder="Enter node information..."
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Location Cards</Label>
                      <Button variant="outline" size="sm" onClick={() => setShowLocationCreator(true)}>
                        Add Location
                      </Button>
                    </div>

                    {/* Existing Locations */}
                    {(node.data.displayOptions || []).filter(opt => opt.type === "LOCATION").length > 0 && (
                      <div className="grid grid-cols-2 gap-2">
                        {(node.data.displayOptions || [])
                          .filter(opt => opt.type === "LOCATION")
                          .map((location, index) => (
                            <div key={location.locationId || `location-${index}`} className="relative group">
                              <div className="rounded-lg border border-gray-100 overflow-hidden">
                                {location.imgUrl && (
                                  <div className="h-24 overflow-hidden">
                                    <img
                                      src={location.imgUrl}
                                      alt={location.title}
                                      className="h-full w-full object-cover"
                                    />
                                  </div>
                                )}
                                <div className="p-2">
                                  <div className="font-medium text-sm">{location.title}</div>
                                  <div className="text-xs text-gray-500">{location.subtitle}</div>
                                  <div className="mt-1">
                                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                                      {location.label}
                                    </span>
                                  </div>
                                  <div className="mt-0.5 text-xs text-gray-600 line-clamp-2">{location.description}</div>
                                </div>
                              </div>
                              
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 rounded-lg">
                                <div className="flex gap-1">
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                      const newLocations = (node.data.displayOptions || []).filter(opt => opt.type === "LOCATION");
                                      const [movedItem] = newLocations.splice(index, 1);
                                      newLocations.splice(Math.max(0, index - 1), 0, movedItem);
                                      const otherOptions = (node.data.displayOptions || []).filter(opt => opt.type !== "LOCATION");
                                      handleUpdate({ displayOptions: [...otherOptions, ...newLocations] });
                                    }}
                                    disabled={index === 0}
                                    className="hover:bg-white/30 h-7 w-7"
                                  >
                                    <ArrowUp className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                      const newLocations = (node.data.displayOptions || []).filter(opt => opt.type === "LOCATION");
                                      const [movedItem] = newLocations.splice(index, 1);
                                      newLocations.splice(Math.min(newLocations.length, index + 1), 0, movedItem);
                                      const otherOptions = (node.data.displayOptions || []).filter(opt => opt.type !== "LOCATION");
                                      handleUpdate({ displayOptions: [...otherOptions, ...newLocations] });
                                    }}
                                    disabled={index === (node.data.displayOptions || []).filter(opt => opt.type === "LOCATION").length - 1}
                                    className="hover:bg-white/30 h-7 w-7"
                                  >
                                    <ArrowDown className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                      const newLocations = (node.data.displayOptions || []).filter(opt => opt.type === "LOCATION").filter((_, i) => i !== index);
                                      const otherOptions = (node.data.displayOptions || []).filter(opt => opt.type !== "LOCATION");
                                      handleUpdate({ displayOptions: [...otherOptions, ...newLocations] });
                                    }}
                                    className="hover:bg-red-500/70 h-7 w-7"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                      setEditingLocation(location);
                                      setShowLocationEditor(true);
                                    }}
                                    className="hover:bg-blue-500/70 h-7 w-7"
                                  >
                                    <Settings className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Location Creator Dialog */}
                    <Dialog open={showLocationCreator} onOpenChange={setShowLocationCreator}>
                      <DialogContent>
                        <LocationEditor 
                          locations={[]}
                          onUpdate={(locations) => {
                            const otherOptions = (node.data.displayOptions || []).filter(opt => opt.type !== "LOCATION");
                            const existingLocations = (node.data.displayOptions || []).filter(opt => opt.type === "LOCATION");
                            handleUpdate({ displayOptions: [...otherOptions, ...existingLocations, ...locations] });
                            setShowLocationCreator(false);
                          }}
                        />
                      </DialogContent>
                    </Dialog>

                    {/* Location Editor Dialog */}
                    {editingLocation && (
                      <LocationDetailEditor
                        location={editingLocation}
                        open={showLocationEditor}
                        onClose={() => {
                          setShowLocationEditor(false);
                          setEditingLocation(null);
                        }}
                        onSave={(updatedLocation) => {
                          const newLocations = (node.data.displayOptions || []).map(opt => {
                            if (opt.type === "LOCATION" && opt.locationId === updatedLocation.locationId) {
                              return updatedLocation;
                            }
                            return opt;
                          });
                          handleUpdate({ displayOptions: newLocations });
                        }}
                      />
                    )}
                  </TabsContent>

                  <TabsContent value="chat" className="space-y-6 p-4 rounded-b-lg">
                    <div className="space-y-2">
                      <Label>Chat Message</Label>
                      <Textarea
                        value={node.data.chatMessage || ''}
                        onChange={(e) => handleQuietUpdate({ chatMessage: e.target.value })}
                        className="h-32"
                        placeholder="Enter chat message..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        Postscript
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="h-4 w-4" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Content that will be loaded into chat after the node data</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </Label>
                      <div className="flex flex-col gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setShowPostscriptEditor(true)}
                          className="w-full"
                        >
                          Edit Postscript Content
                        </Button>
                        {(node.data.postscript || []).map((item, index) => (
                          <div key={index} className="text-sm text-gray-600 p-2 bg-gray-50 rounded">
                            {item.type === "CHAT" && (
                              <div className="flex items-center gap-2">
                                <MessageSquare className="h-4 w-4" />
                                <span className="truncate">{item.content}</span>
                              </div>
                            )}
                            {item.type === "HTML" && (
                              <div className="flex items-center gap-2">
                                <Code className="h-4 w-4" />
                                <span className="truncate">{item.content}</span>
                              </div>
                            )}
                            {item.type === "LOCATION" && (
                              <div className="flex items-center gap-2">
                                <Component className="h-4 w-4" />
                                <span className="truncate">{item.title}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        Suggestions
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="h-4 w-4" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Add suggested responses for the chat</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          value={newSuggestion}
                          onChange={(e) => setNewSuggestion(e.target.value)}
                          placeholder="Add a suggestion..."
                          onKeyDown={(e) => e.key === 'Enter' && addSuggestion()}
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={addSuggestion}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {node.data.suggestions?.map((suggestion, index) => (
                          <div key={index} className="flex items-center gap-2 group">
                            <Input
                              value={suggestion}
                              onChange={(e) => {
                                const updatedSuggestions = [...(node.data.suggestions || [])];
                                updatedSuggestions[index] = e.target.value;
                                handleUpdate({ suggestions: updatedSuggestions });
                              }}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteSuggestion(index)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="component" className="space-y-6 p-4 rounded-b-lg">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Component Type</Label>
                        <Input
                          value={componentType}
                          onChange={(e) => {
                            const newType = e.target.value;
                            setComponentType(newType);
                            console.log('Updating component type to:', newType);
                            handleQuietUpdate({
                              component: {
                                type: newType,
                                props: node.data.component?.props || {}
                              }
                            });
                          }}
                          placeholder="Enter component type..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Props (JSON)</Label>
                        <Textarea
                          value={componentProps}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            setComponentProps(newValue);
                            try {
                              const props = JSON.parse(newValue);
                              console.log('Updating component props to:', props);
                              handleQuietUpdate({
                                component: {
                                  type: node.data.component?.type || '',
                                  props
                                }
                              });
                            } catch (error) {
                              // Invalid JSON, ignore
                            }
                          }}
                          className="h-48 font-mono"
                          placeholder="{}"
                        />
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="code" className="space-y-6 p-4 rounded-b-lg">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>HTML</Label>
                        <Textarea
                          value={node.data.html || ''}
                          onChange={(e) => handleUpdate({ html: e.target.value })}
                          className="h-48 font-mono"
                          placeholder="Enter HTML code..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Function Calls</Label>
                        <Textarea
                          value={JSON.stringify(node.data.functionCalls || [], null, 2)}
                          onChange={(e) => {
                            try {
                              const functionCalls = JSON.parse(e.target.value);
                              handleUpdate({ functionCalls });
                            } catch (error) {
                              // Invalid JSON, ignore
                            }
                          }}
                          placeholder="[]"
                          className="font-mono h-48"
                        />
                      </div>
                    </div>
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            </div>
          </SheetContent>
        </Sheet>

        <PostscriptEditor
          postscript={node.data.postscript || []}
          open={showPostscriptEditor}
          onClose={() => setShowPostscriptEditor(false)}
          onSave={(postscript) => {
            handleUpdate({ postscript });
            setShowPostscriptEditor(false);
          }}
        />

        <Dialog open={showAddToLibraryDialog} onOpenChange={setShowAddToLibraryDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add to Library</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="libraryNodeName">Node Name</Label>
                <Input
                  id="libraryNodeName"
                  value={libraryNodeName}
                  onChange={(e) => setLibraryNodeName(e.target.value)}
                  placeholder="Enter node name"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddToLibraryDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddToLibrary}>
                Add to Library
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="p-6 rounded-lg">
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure you want to delete this node?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the node and all its children. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => {
                  onDelete(node.id);
                  setShowDeleteDialog(false);
                }}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
  );
}